<template>
  <div>
    <CommonTabs
      v-if="isMobile"
      :list="contentTypes"
      :initial-tab-index="activeTabIndex"
      is-collection
      class="mb-8"
      @on-change="handleActiveTab"
    />
    <div v-else class="my-12 flex justify-between">
      <CommonTabs
        :list="contentTypes"
        :initial-tab-index="activeTabIndex"
        is-collection
      />
      <TeacherTileToggler
        class="ml-10 hidden h-fit lg:flex"
        :active-tile="activeIndexTile"
        :icons="groupList"
        @change-group="handleActiveTile"
      />
    </div>
    <div
      v-if="sortedCollection.length"
      class="wrap-group"
      :class="[
        groupClass,
        {
          'rounded-xl border-b border-[#F3F3F3] shadow-drop-2':
            groupClass !== 'tile' && !isDates,
        },
        { '-m-2.5': groupClass === 'tile' },
      ]"
    >
      <template
        v-for="(card, index) in sortedCollection"
        :key="`${card.id}_${index}`"
      >
        <Component
          :is="
            isPresentation
              ? 'div'
              : (card?.outer_link || card?.url) && !isBooks
              ? 'a'
              : NuxtLink
          "
          v-bind="getAttributes(card)"
          class="group-card"
          :class="[
            { 'border-b border-[#F3F3F3]': groupClass !== 'tile' },
            { 'books-card': isBooks },
            { date: isDates },
          ]"
        >
          <Component
            :is="isComponent"
            :data="card"
            :category-title="category.title"
            :scool-class="category.school_class"
            disable-announce
            @click:presentation="showPresentation(card)"
          />
        </Component>
      </template>
    </div>
  </div>
</template>

<script setup>
const NuxtLink = resolveComponent('NuxtLink');
const DateCard = resolveComponent('MainPageCardsReworkedTeacherDateCard');
const BooksCard = resolveComponent('MainPageCardsReworkedTeacherBooksCard');
const ContentCard = resolveComponent('MainPageCardsReworkedTeacherContentCard');
const ContentList = resolveComponent('MainPageCardsReworkedTeacherContentList');
const PresentationCard = resolveComponent(
  'MainPageCardsReworkedTeacherPresentationCard',
);

definePageMeta({
  layout: 'marginless',
});

const props = defineProps({
  highlight: {
    type: Object,
    default: () => ({}),
  },
  data: {
    type: Array,
    default: () => [],
  },
  category: {
    type: Object,
    default: () => ({}),
  },
});

const route = useRoute();
const router = useRouter();
const modalsStore = useModalsStore();
const { isMobile, getScreenBreakpoint, isHorizontalPadOrMore } =
  useBreakpoint();

const activeTab = ref(null);
const EXCLUDED_CONTENT_TYPES = [
  'biography',
  'document',
  // 'document_collection',
  'illustration',
  'region_heraldic',
  'region_route',
  'region_article',
  // 'presentation',
  ...(props.category?.shelf_id === 7
    ? ['audiomaterial', 'date']
    : ['presentation']),
];

const isWiki = computed(() => {
  return route.query.content === 'wiki';
});

const groupList = computed(() => {
  if (isWiki.value || isDates.value) return ['list'];
  return ['tile', 'list'];
});

const activeTile = computed(() => {
  if (isWiki.value || isDates.value) return 'list';
  return route.query.group || 'tile';
});

const activeIndexTile = computed(() => {
  return groupList.value?.findIndex((g) => g === activeTile.value);
});

const groupClass = computed(() => {
  if (isWiki.value || isDates.value) return 'list';
  if (['xs', 'sm', 'md'].includes(getScreenBreakpoint.value)) return 'tile';
  return groupList.value[activeIndexTile.value];
});

const isBooks = computed(() => {
  return route.query.content === 'book';
});

const isDates = computed(() => {
  return route.query.content === 'date';
});

const isPresentation = computed(() => {
  return route.query.content === 'presentation';
});

const isComponent = computed(() => {
  if (isPresentation.value && groupClass.value === 'tile')
    return PresentationCard;
  if (isDates.value) return DateCard;
  if (isWiki.value) return ContentList;
  if (isBooks.value && groupClass.value === 'tile') return BooksCard;
  if (!isBooks.value && groupClass.value === 'tile') return ContentCard;
  return ContentList;
});

const sortedCollection = computed(() => {
  return props.data ?? [];
});

const contentTypes = computed(() => {
  return props.highlight?.content_types?.filter(
    ({ model }) => !EXCLUDED_CONTENT_TYPES.includes(model),
  );
});

const activeTabIndex = computed(() => {
  const idx = contentTypes.value?.findIndex(
    ({ model }) => model === getModelType(route.query.content),
  );

  if (idx !== -1) return idx;
  const availableIdx = contentTypes.value?.findIndex(({ count }) => count > 0);

  if (availableIdx !== -1) return availableIdx;
  return 0;
});

/**methods */
const getAttributes = (card) => {
  if (isDates.value) return {};
  return {
    ...(isBooks.value && { to: getBookUrl(card) }),
    ...(!isBooks.value &&
      !(card?.outer_link || card?.url) && { to: getUrl(card) }),
    ...((card?.outer_link || card?.url) &&
      !isBooks.value && { href: getUrl(card) }),
    ...(!isBooks.value && card?.outer_link && { target: '_blank' }),
  };
};

const handleActiveTile = (idx) => {
  if (idx === activeIndexTile.value) return;

  router.replace({
    query: {
      ...route.query,
      group: idx ? groupList.value[idx] : undefined,
    },
  });
};

const getModelType = (modelType) => {
  let itemModel = '';
  switch (modelType) {
    case 'audiolecture':
    case 'audiomaterial':
      itemModel = 'audiomaterial';
      break;
    case 'videolecture':
    case 'videomaterial':
      itemModel = 'videomaterial';
      break;
    default:
      itemModel = modelType;
      break;
  }
  return itemModel;
};

const handleActiveTab = (tab) => {
  if (tab?.model && route.query.content !== tab?.model) {
    router.replace({
      query: {
        ...route.query,
        content: tab.model,
      },
    });
  }

  activeTab.value = tab;
};

const getUrl = (card) => {
  return (
    card?.outer_link ??
    card?.url ??
    `/teacher/${route.params.parent}/${route.params.category}/${
      card.model_type
    }/${card.slug || card.id}?content=${getModelType(
      card.model_type,
    )}${modernIsGroup(groupClass.value)}`
  );
};

const getBookUrl = (card) => {
  return `/teacher/${route.params.parent}/${route.params.category}/${
    card.model_type
  }/${card.slug}?content=${getModelType(card.model_type)}${modernIsGroup(
    groupClass.value,
  )}`;
};

const showPresentation = (card) => {
  isHorizontalPadOrMore.value && document.documentElement.requestFullscreen();
  modalsStore.setPropertiesModal({
    slides: card.slides,
    fullscreen: isHorizontalPadOrMore.value,
  });
  modalsStore.showGalleryPopup();
};

watch(
  () => route.query.content,
  () => {
    if (route.query?.content) {
      const queryTab = contentTypes.value?.find(
        ({ model }) => model === getModelType(route.query.content),
      );
      handleActiveTab(queryTab);
    } else {
      handleActiveTab(props.highlight?.content_types?.[0]);
    }
  },
  { immediate: true },
);

useHead(
  computed(() => ({
    title: `${
      props.highlight?.title ? props.highlight?.title : 'История РФ'
    } | Материалы по истории для школьного курса «История России» и «Всеобщая история» с 5 по 11 класс`,
    meta: [
      {
        name: 'description',
        content:
          'История Российской Федерации в статьях. Освещаем все вехи российской истории в статьях. Российское военно-историческое общество. Мы знаем правду.',
      },
      {
        name: 'keywords',
        content: 'История РФ: статьи для школьников и студентов',
      },
    ],
  })),
);
</script>

<style lang="scss">
.wrap-group {
  @apply flex flex-wrap;

  .group-card {
    @apply w-full sm:w-1/2 lg:w-1/3 xl:w-1/4 2xl:w-1/5 p-2.5;
  }
  .group-card.date {
    @apply p-0;
  }
  .group-card.books-card {
    @apply w-full lg:w-1/2 2xl:w-1/3 p-2.5;
  }

  &.list {
    @apply flex-col;

    .group-card {
      @apply w-full;
    }
  }
}
</style>
